/**
 * AWS Amplify Configuration
 * Configures authentication with Cognito User Pool in Sydney region
 * Data sovereignty compliant - all services in ap-southeast-2
 * 
 * Format: AWS Amplify v6.0.7 Compatible
 */

import { Amplify } from 'aws-amplify';
import envConfig from './envConfig';

let config = null;

/**
 * Initialize Amplify configuration from Parameter Store
 * @returns {Promise<Object>} - Amplify configuration object
 */
const initializeAmplifyConfig = async () => {
  try {
    if (!config) {
      console.log('🔧 Initializing Amplify configuration...');
      config = envConfig.initializeConfig();
    }

    const amplifyConfig = {
      Auth: {
        Cognito: {
          userPoolId: config.cognito.userPoolId,
          userPoolClientId: config.cognito.userPoolClientId,
          region: config.cognito.region || 'ap-southeast-2',
          loginWith: {
            oauth: {
              domain: config.cognito.domain,
              scopes: ['openid', 'email', 'profile'],
              redirectSignIn: [
                'http://localhost:3000/',
                'https://d21ar8rqmxxz1f.cloudfront.net/'
              ],
              redirectSignOut: [
                'http://localhost:3000/',
                'https://d21ar8rqmxxz1f.cloudfront.net/'
              ],
              responseType: 'code'
            }
          }
        }
      },
      API: {
        REST: {
          AfterServiceAPI: {
            endpoint: config.api.endpoint,
            region: config.api.region || 'ap-southeast-2'
          }
        }
      }
    };

    // Log the configuration for debugging
    console.log('🔧 Amplify Auth Config:', {
      userPoolId: config.cognito.userPoolId,
      userPoolClientId: config.cognito.userPoolClientId,
      region: config.cognito.region || 'ap-southeast-2',
      domain: config.cognito.domain
    });

    console.log('✅ Amplify configuration initialized successfully');
    return amplifyConfig;
  } catch (error) {
    console.error('❌ Error initializing Amplify configuration:', error);
    
    // Fallback configuration
    console.log('🔄 Using fallback Amplify configuration');
    const fallbackConfig = {
      Auth: {
        Cognito: {
          userPoolId: envConfig.getCognitoConfig().userPoolId,
          userPoolClientId: envConfig.getCognitoConfig().userPoolClientId,
          region: envConfig.getCognitoConfig().region || 'ap-southeast-2',
          loginWith: {
            oauth: {
              domain: envConfig.getCognitoConfig().domain,
              scopes: ['openid', 'email', 'profile'],
              redirectSignIn: [
                'http://localhost:3000/',
                'https://d21ar8rqmxxz1f.cloudfront.net/'
              ],
              redirectSignOut: [
                'http://localhost:3000/',
                'https://d21ar8rqmxxz1f.cloudfront.net/'
              ],
              responseType: 'code'
            }
          }
        }
      },
      API: {
        REST: {
          AfterServiceAPI: {
            endpoint: envConfig.getApiConfig().endpoint,
            region: envConfig.getApiConfig().region
          }
        }
      }
    };

    // Log the fallback configuration for debugging
    console.log('🔧 Using Fallback Auth Config:', {
      userPoolId: envConfig.getCognitoConfig().userPoolId,
      userPoolClientId: envConfig.getCognitoConfig().userPoolClientId,
      region: envConfig.getCognitoConfig().region || 'ap-southeast-2',
      domain: envConfig.getCognitoConfig().domain
    });
    
    return fallbackConfig;
  }
};

/**
 * Configure Amplify with the loaded configuration
 * @returns {Promise<Object>} - Configured Amplify instance
 */
const configureAmplify = async () => {
  try {
    const amplifyConfig = await initializeAmplifyConfig();
    Amplify.configure(amplifyConfig);
    console.log('✅ Amplify configured successfully');
    return amplifyConfig;
  } catch (error) {
    console.error('❌ Error configuring Amplify:', error);
    throw error;
  }
};

/**
 * Get the current Amplify configuration
 * @returns {Promise<Object>} - Current configuration
 */
const getAmplifyConfig = async () => {
  try {
    console.log('🔧 getAmplifyConfig called, generating fresh config...');
    const amplifyConfig = await initializeAmplifyConfig();
    console.log('🔧 getAmplifyConfig returning:', {
      hasAuth: !!amplifyConfig.Auth,
      hasCognito: !!amplifyConfig.Auth?.Cognito,
      userPoolId: amplifyConfig.Auth?.Cognito?.userPoolId,
      userPoolClientId: amplifyConfig.Auth?.Cognito?.userPoolClientId
    });
    return amplifyConfig;
  } catch (error) {
    console.error('❌ getAmplifyConfig failed:', error);
    throw error;
  }
};

/**
 * Get specific configuration sections
 */
const getApiConfig = () => config?.api || envConfig.getApiConfig();
const getCognitoConfig = () => config?.cognito || envConfig.getCognitoConfig();
const getBedrockConfig = () => config?.bedrock || envConfig.getBedrockConfig();

export {
  initializeAmplifyConfig,
  configureAmplify,
  getAmplifyConfig,
  getApiConfig,
  getCognitoConfig,
  getBedrockConfig
};

// Form fields configuration
export const formFields = {
  signIn: {
    username: {
      label: 'Email',
      placeholder: 'Enter your email',
      isRequired: true,
      order: 1
    },
    password: {
      label: 'Password',
      placeholder: 'Enter your password',
      isRequired: true,
      order: 2
    }
  },
  signUp: {
    email: {
      order: 1,
      placeholder: 'Enter your email address',
      label: 'Email Address *',
      inputProps: { required: true, type: 'email' }
    },
    given_name: {
      order: 2,
      placeholder: 'Enter your first name',
      label: 'First Name *',
      inputProps: { required: true }
    },
    family_name: {
      order: 3,
      placeholder: 'Enter your last name', 
      label: 'Last Name *',
      inputProps: { required: true }
    },
    password: {
      order: 4,
      placeholder: 'Enter your password',
      label: 'Password *',
      inputProps: { required: true, type: 'password' }
    },
    confirm_password: {
      order: 5,
      placeholder: 'Confirm your password',
      label: 'Confirm Password *',
      inputProps: { required: true, type: 'password' }
    }
  },
  forceNewPassword: {
    password: {
      label: 'New Password',
      placeholder: 'Enter your new password',
      isRequired: true,
      order: 1
    }
  },
  resetPassword: {
    username: {
      label: 'Email Address',
      placeholder: 'Enter your email address',
      isRequired: true,
      order: 1,
      inputProps: { 
        type: 'email',
        autocomplete: 'username'
      }
    }
  },
  confirmResetPassword: {
    confirmation_code: {
      label: 'Verification Code',
      placeholder: 'Enter 6-digit code from email',
      isRequired: true,
      order: 1,
      inputProps: { 
        type: 'text',
        maxLength: 6,
        pattern: '[0-9]*',
        autocomplete: 'one-time-code'
      }
    },
    password: {
      label: 'New Password',
      placeholder: 'Enter your new password',
      isRequired: true,
      order: 2,
      inputProps: { 
        type: 'password',
        autocomplete: 'new-password'
      }
    }
  }
};

// Default export
export default {
  initializeAmplifyConfig,
  configureAmplify,
  getAmplifyConfig,
  getApiConfig,
  getCognitoConfig,
  getBedrockConfig,
  formFields
}; 